import { queryOptions } from '@tanstack/react-query'
import { isNotFound, type ApiClient } from '@volee/api-client'
import type { components } from '@volee/api-types'

import { enrolmentLinkMemory } from './enrolment-link-memory.ts'
import type { PublicClubRead } from './registration.ts'
import { api } from '../services/api.ts'

// The three calls of the family enrolment flow, all public: nobody holds an account yet at
// this stage, and a token in the URL is the only credential any of them carries.

export type TeamInviteLinkPublic = components['schemas']['TeamInviteLinkPublicRead']
export type TeamBrief = components['schemas']['TeamBrief']
export type RosterSearch = components['schemas']['RosterSearch']
export type RosterMatch = components['schemas']['RosterMatchRead']
export type FamilyEnrolmentCreate = components['schemas']['FamilyEnrolmentCreate']
export type FamilyEnrolmentBody = components['schemas']['FamilyEnrolmentBody']
export type ChildEnrolment = components['schemas']['ChildEnrolment']
export type FamilyEnrolmentSubmitted = components['schemas']['FamilyEnrolmentSubmitted']
export type FamilyEnrolmentStatusRead = components['schemas']['FamilyEnrolmentStatusRead']
export type GuardianLink = components['schemas']['GuardianLinkRead']

/** The club the wizard enrols into, whichever way it was named: the link's short name and
 *  crest, or the picker's legal name and crest. Only a link names the leva. */
export interface EnrolmentClub {
  readonly id: number
  readonly name: string
  readonly logo: string | null
  readonly team: TeamBrief | null
}

/** How the wizard learns its club: from the link's token, or from a club already chosen. */
export type ClubSource =
  | { readonly kind: 'token'; readonly token: string }
  | { readonly kind: 'club'; readonly club: EnrolmentClub }

export function clubOfLink(link: TeamInviteLinkPublic): EnrolmentClub {
  return {
    id: link.club_id,
    name: link.club_short_name,
    logo: link.logo_display_url ?? null,
    team: link.team,
  }
}

/** The picker's twin of `clubOfLink`: the legal name, which is what the picker showed, and no
 *  leva — the picker names a club, not a squad within it. */
export function clubOfPick(club: PublicClubRead): EnrolmentClub {
  return { id: club.id, name: club.name, logo: club.logo_display_url ?? null, team: null }
}

/* The states the status screen branches on, as the contract names them. Listed and not only
 * typed: `familyEnrolmentStatusOf` checks a value at runtime, where a type cannot. */
export const FAMILY_ENROLMENT_STATUSES = [
  'awaiting_guardian',
  'submitted',
  'guardian_declined',
  'expired',
  'rejected',
  'approved',
] as const satisfies readonly FamilyEnrolmentStatusRead['status'][]

export type FamilyEnrolmentStatus = (typeof FAMILY_ENROLMENT_STATUSES)[number]

/** The status the row carries, or `null` for a value this build does not know — a fourth
 *  state added server-side must not render as a blank screen. */
export function familyEnrolmentStatusOf(status: string): FamilyEnrolmentStatus | null {
  return (FAMILY_ENROLMENT_STATUSES as readonly string[]).includes(status)
    ? (status as FamilyEnrolmentStatus)
    : null
}

/** `authenticated: false` on purpose: another account's still-valid Bearer on this call
 *  would make its 401 fire `onSessionExpired` in the middle of a family's own enrolment. */
export function resolveTeamLink(client: ApiClient, token: string): Promise<TeamInviteLinkPublic> {
  return client.get<TeamInviteLinkPublic>(
    `/v1/public/team-invite-links/${encodeURIComponent(token)}`,
    { authenticated: false },
  )
}

export function searchRoster(
  client: ApiClient,
  token: string,
  body: RosterSearch,
): Promise<RosterMatch> {
  return client.post<RosterMatch>(
    `/v1/public/team-invite-links/${encodeURIComponent(token)}/roster-search`,
    body,
    { authenticated: false },
  )
}

export type PrivacyNotice = components['schemas']['PrivacyNoticeRead']
export const FAMILY_ENROLMENT_NOTICE = 'family_enrolment'

export function fetchPrivacyNotice(client: ApiClient, code: string): Promise<PrivacyNotice> {
  return client.get<PrivacyNotice>(`/v1/public/privacy-notices/${encodeURIComponent(code)}`, {
    authenticated: false,
  })
}

/** Defaults to anonymous, mirroring `submitFamilyEnrolmentByClub`: `true` carries the
 *  session's token, for the family the wizard finds already signed in (#4). */
export function submitFamilyEnrolment(
  client: ApiClient,
  body: FamilyEnrolmentCreate,
  authenticated = false,
): Promise<FamilyEnrolmentSubmitted> {
  return client.post<FamilyEnrolmentSubmitted>('/v1/public/family-enrolment-requests', body, {
    authenticated,
  })
}

// The same two acts by club id, for the sign-up that chose the club instead of following a
// link (issue #95). The server decides whether that club may take a request.

export function searchRosterByClub(
  client: ApiClient,
  clubId: number,
  body: RosterSearch,
): Promise<RosterMatch> {
  return client.post<RosterMatch>(`/v1/public/clubs/${String(clubId)}/roster-search`, body, {
    authenticated: false,
  })
}

/** Defaults to anonymous; the wizard passes `true` when it finds a session already open — see
 *  apps/frontend/docs/accesso.md. */
export function submitFamilyEnrolmentByClub(
  client: ApiClient,
  clubId: number,
  body: FamilyEnrolmentBody,
  authenticated = false,
): Promise<FamilyEnrolmentSubmitted> {
  return client.post<FamilyEnrolmentSubmitted>(
    `/v1/public/clubs/${String(clubId)}/family-enrolment-requests`,
    body,
    { authenticated },
  )
}

/** «Aggiungi un figlio»: always with the session, which is who confirms. The club travels in the body. */
export function addChild(
  client: ApiClient,
  body: ChildEnrolment,
): Promise<FamilyEnrolmentSubmitted> {
  return client.post<FamilyEnrolmentSubmitted>('/v1/me/children', body)
}

/** Authenticated, and deliberately through the subject token rather than `current_user`:
 *  the player holds a session but no account until the club decides. */
export function readOwnEnrolment(client: ApiClient): Promise<FamilyEnrolmentStatusRead> {
  return client.get<FamilyEnrolmentStatusRead>('/v1/family-enrolment-requests/me')
}

/** «Manda un nuovo link»: the same link rewritten for seven days more, the request reopened. On the
 *  subject token, as `readOwnEnrolment`; it answers the request as it stands after. */
export function sendTheGuardianLinkAgain(client: ApiClient): Promise<FamilyEnrolmentStatusRead> {
  return client.post<FamilyEnrolmentStatusRead>('/v1/family-enrolment-requests/me/guardian-link')
}

/** «Manda un nuovo link» from a phone with no account, by the link it keeps: `authenticated: false`,
 *  as `readEnrolmentByLink`. The kept token dies, and the answer carries the new one. */
export function sendTheGuardianLinkAgainByLink(
  client: ApiClient,
  token: string,
): Promise<FamilyEnrolmentStatusRead> {
  return client.post<FamilyEnrolmentStatusRead>(
    '/v1/public/guardian-link/again',
    { token },
    { authenticated: false },
  )
}

/** Turned 14 while waiting with no mail (#300): a code to the child's own box, by the link kept. */
export function requestOwnEmail(client: ApiClient, token: string, email: string): Promise<void> {
  return client.post<void>(
    '/v1/public/guardian-link/own-email',
    { token, email },
    { authenticated: false },
  )
}

export interface OwnAccess {
  readonly token: string
  readonly email: string
  readonly code: string
  readonly password: string
}

/** The code and the password: the child's own access, and the request the club's. */
export function confirmOwnAccess(
  client: ApiClient,
  access: OwnAccess,
): Promise<FamilyEnrolmentStatusRead> {
  return client.post<FamilyEnrolmentStatusRead>(
    '/v1/public/guardian-link/own-email/confirm',
    { ...access },
    { authenticated: false },
  )
}

/** The same page, read by the link a child with no account keeps: `authenticated: false`, the
 *  token is the credential. */
export function readEnrolmentByLink(
  client: ApiClient,
  token: string,
): Promise<FamilyEnrolmentStatusRead> {
  return client.get<FamilyEnrolmentStatusRead>('/v1/public/guardian-link/request', {
    query: { token },
    authenticated: false,
  })
}

// Indirection with a purpose: a test replaces these, whereas a spy on this module's own
// exports cannot rebind a call the module makes to itself.
export const familyEnrolmentReader = {
  resolveTeamLink: (token: string) => resolveTeamLink(api, token),
  readOwn: () => readOwnEnrolment(api),
  readByLink: (token: string) => readEnrolmentByLink(api, token),
  readNotice: (code: string) => fetchPrivacyNotice(api, code),
}

export const familyEnrolmentWriter = {
  sendTheLinkAgain: () => sendTheGuardianLinkAgain(api),
  sendTheLinkAgainByLink: (token: string) => sendTheGuardianLinkAgainByLink(api, token),
  requestOwnEmail: (token: string, email: string) => requestOwnEmail(api, token, email),
  confirmOwnAccess: (access: OwnAccess) => confirmOwnAccess(api, access),
  searchRoster: (token: string, body: RosterSearch) => searchRoster(api, token, body),
  submit: (body: FamilyEnrolmentCreate, authenticated = false) =>
    submitFamilyEnrolment(api, body, authenticated),
  searchRosterByClub: (clubId: number, body: RosterSearch) => searchRosterByClub(api, clubId, body),
  submitByClub: (clubId: number, body: FamilyEnrolmentBody, authenticated = false) =>
    submitFamilyEnrolmentByClub(api, clubId, body, authenticated),
  addChild: (body: ChildEnrolment) => addChild(api, body),
}

export const TEAM_INVITE_LINK_PUBLIC_KEY = 'team-invite-link-public'

/** A 404 is THE answer — the link is spent or was never valid — as opposed to a transient
 *  failure (#2), which `useFamilyEnrolment` offers a manual retry for instead. */
export function isMissingLink(error: unknown): boolean {
  return isNotFound(error)
}

/** `retry: false`: automatic retries would only delay either screen — the 404 that says the
 *  link is dead, or the failure screen that offers its own «Riprova» (#2). */
export function clubLinkQueryOptions(token: string) {
  return queryOptions({
    queryKey: [TEAM_INVITE_LINK_PUBLIC_KEY, token] as const,
    queryFn: () => familyEnrolmentReader.resolveTeamLink(token),
    retry: false,
    staleTime: Infinity,
  })
}

/** The consent text: one code, shared by the link and the club-door wizards (R16a) — a
 *  `staleTime` of `Infinity` because a published notice does not change under a family
 *  mid-wizard. */
export function privacyNoticeQueryOptions(code: string) {
  return queryOptions({
    queryKey: ['privacy-notice', code] as const,
    queryFn: () => familyEnrolmentReader.readNotice(code),
    staleTime: Infinity,
  })
}

export const FAMILY_ENROLMENT_STATUS_KEY = ['family-enrolment-request', 'me'] as const

/** `retry: false` as in `registrationStatusQueryOptions`: a caller with no request gets a
 *  404, and that is THE answer — retrying only delays the screen that acts on it. */
/** By the link a phone with no account kept, or by the session. A link replaced or gone is
 *  forgotten, and the session answers instead. */
async function readStatus(): Promise<FamilyEnrolmentStatusRead> {
  const token = enrolmentLinkMemory.read()
  if (token === null) return familyEnrolmentReader.readOwn()
  try {
    return await familyEnrolmentReader.readByLink(token)
  } catch (error) {
    if (!isNotFound(error)) throw error
    enrolmentLinkMemory.forget()
    return familyEnrolmentReader.readOwn()
  }
}

export const familyEnrolmentStatusQueryOptions = queryOptions({
  queryKey: FAMILY_ENROLMENT_STATUS_KEY,
  queryFn: readStatus,
  retry: false,
})
